import type { DefinitionsMetadataApi } from '../api/DefinitionsMetadataApi'
import type { CustomGroup, Grouping, GroupingDraft } from '../models/grouping.types'
import { diffCustomGroups } from '../service/groupRules'

export type GroupingValidationError = 'name' | 'level' | 'groups'

/** One custom group that did not persist, carrying the error the server actually returned. */
export interface CustomGroupFailure {
  name: string
  error: unknown
}

export type SaveGroupingResult =
  | { ok: false; error: GroupingValidationError }
  | { ok: true; groupingId: number | string | null; failed: CustomGroupFailure[] }

export interface SaveGroupingDependencies {
  api: Pick<
    DefinitionsMetadataApi,
    | 'createGrouping'
    | 'updateGrouping'
    | 'createCustomGroup'
    | 'updateCustomGroup'
    | 'deleteCustomGroup'
  >
}

export interface SaveGroupingInput {
  draft: GroupingDraft
  accountId: number | string
  workspaceId: number | string
  /** The persisted grouping this draft came from — the delete side of the diff needs it. */
  original?: Grouping
}

function validate(draft: GroupingDraft): GroupingValidationError | null {
  if (!draft.name.trim()) return 'name'
  if (draft.id === undefined && !draft.entity_type.trim()) return 'level'
  if (draft.custom_groups.some((group) => !group.name.trim())) return 'groups'
  return null
}

/**
 * Save a grouping and reconcile its custom groups — the v4 replacement for `GroupModal.complete`
 * (`Orphex-New-Front/.../Grouping/GroupModal.vue:172-274`) and the fix for **D-30-4**.
 *
 * v3 wrapped every request in
 *
 *     request.catch(() => { throw new Error(groupName || 'This group') })   // :167-170
 *
 * and rendered the result as `` `${err.message} has definition problems, please check your
 * conditions` `` (`:269`). The real error — status, backend validation message, an offline network
 * failure — was thrown away, so a dropped connection surfaced to the user as
 * *"Network Error has definition problems, please check your conditions"*.
 *
 * Here the grouping write still aborts the save (there is nothing to fan out to without an id),
 * but each custom-group request is settled independently: successes are kept, the grouping is NOT
 * rolled back — matching legacy behaviour — and every failure is returned with its own untouched
 * error for the caller to render.
 */
export class SaveGroupingFlow {
  constructor(private readonly dependencies: SaveGroupingDependencies) {}

  async save(input: SaveGroupingInput): Promise<SaveGroupingResult> {
    const error = validate(input.draft)
    if (error) return { ok: false, error }

    const groupingId = await this.writeGrouping(input)
    const diff = diffCustomGroups(input.original?.custom_groups ?? [], input.draft.custom_groups)

    const tasks: Array<{ name: string; run: () => Promise<unknown> }> = [
      ...diff.deleted.map((group) => ({
        name: group.name,
        run: () =>
          this.dependencies.api.deleteCustomGroup(input.accountId, input.workspaceId, group.id),
      })),
      ...diff.updated.map((group) => ({
        name: group.name,
        run: () =>
          this.dependencies.api.updateCustomGroup(
            input.accountId,
            input.workspaceId,
            group.id,
            group,
          ),
      })),
      ...diff.created.map((group) => ({
        name: group.name,
        run: () => this.createGroup(input, groupingId, group),
      })),
    ]

    const settled = await Promise.allSettled(tasks.map((task) => task.run()))
    const failed = settled.flatMap((outcome, index) =>
      outcome.status === 'rejected'
        ? [{ name: tasks[index]?.name ?? '', error: outcome.reason as unknown }]
        : [],
    )
    return { ok: true, groupingId, failed }
  }

  private async writeGrouping(input: SaveGroupingInput): Promise<number | string | null> {
    if (input.draft.id !== undefined) {
      await this.dependencies.api.updateGrouping(
        input.accountId,
        input.workspaceId,
        input.draft.id,
        {
          name: input.draft.name,
          description: input.draft.description,
          is_active: true,
        },
      )
      return input.draft.id
    }

    const created = await this.dependencies.api.createGrouping(input.accountId, input.workspaceId, {
      name: input.draft.name,
      description: input.draft.description,
      entity_type: input.draft.entity_type,
      is_active: true,
    })
    return created?.id ?? null
  }

  private async createGroup(
    input: SaveGroupingInput,
    groupingId: number | string | null,
    group: CustomGroup,
  ): Promise<unknown> {
    if (groupingId === null) {
      // v3 posted to `/groupings/null/custom-groups/` when the create response carried no id
      // (`GroupModal.vue:202`), producing a 404 blamed on "definition problems".
      throw new Error('grouping-id-missing')
    }
    return this.dependencies.api.createCustomGroup(
      input.accountId,
      input.workspaceId,
      groupingId,
      group,
    )
  }
}
